import React from 'react';
import ReactDOM from 'react-dom/client';
import jss from 'jss';
import JSSPluginNested from 'jss-plugin-nested';
import { JssProvider } from 'react-jss';
import whyDidYouRender from '@welldone-software/why-did-you-render';
import { createGenerateClassName, jssPreset } from '@material-ui/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
// Modified by AI on 06/29/2026. Edit #1.
import queryClient from './queryClient';
import { AppWithSettings } from './contexts/SettingsContext';
import { AppWithStaticData } from './contexts/StaticDataContext';
import { MobileApp } from './contexts/MobileContext';
import { PublicApp } from './contexts/PublicContext';
import { PrintableApp } from './contexts/PrintContext';
import { SharedApp } from './contexts/SharedContext';
import { AuthorizedApp } from './contexts/AuthorizationContext';
import { EmployerApp } from './contexts/EmployerContext';
import { RecruiterApp } from './contexts/RecruiterContext';
import { PositionApp } from './contexts/PositionContext';
import { AppWithNotification } from './contexts/NotificationContext';
import { AppWithModal } from './contexts/ModalContext';
import { AppWithSidePanel } from './contexts/SidePanelContext';
import { LoadingApp } from './contexts/LoadingContext';
import { AppWithExplains } from './contexts/ExplainContext';
import { TPAHealthMeterApp } from './contexts/TPAHealthMeterContext';
import { AppWithEvents } from './contexts/ApplicationEventsContext';
import { TrackingApp } from './contexts/TrackingContext';
import ThemedApp from './contexts/ThemeContext';
import Application from './application/Application';
import i18n from './services/i18n';
import checkLocalStorage from './services/localStorageChecker';
import registerRaven from './utils/registerRaven';
import registerHotJar from './utils/registerHotJar';
import './assets/css/style.css';
import { AppWithGlobalState } from './contexts/GlobalStateContext';
import { CandidatesLinkedinStatusApp } from './contexts/CandidatesLinkedinStatusContext';
import { LinkedinUserInviteLimitApp } from './contexts/LinkedinUserInviteLimitContext';
import { LinkedinMessagesSendingApp } from './contexts/LinkedinMessagesSendingContext';
import { AppWithAutoCampaign } from './contexts/AutoCampaignContext';
import { UnsubscribeApp } from './contexts/UnsubscribeContext';
import { CampaignApp } from './contexts/CampaignContext';
// Modified by AI on 06/29/2026. Edit #4.
import { LinkedinInMailApp } from './contexts/LinkedinInMailContext';
import { PendoApp } from './contexts/PendoContext';
import { AppWithFeedbackStats } from './contexts/FeedbackStatsContext';
import { AppWithCampaignEvents } from './contexts/CampaignEventsContext';
import { AppWithCandidatesAtsSync } from './contexts/CandidatesAtsSyncContext';
import { AppWithCandidatesStore } from './contexts/CandidatesStoreContext';

checkLocalStorage(BUILD_DATE);
registerRaven();
registerHotJar();

const generateClassName = createGenerateClassName({
    dangerouslyUseGlobalCSS: false,
    productionPrefix: 'c',
});

jss.use(JSSPluginNested());

const jssConfig = jss.setup({
    ...jssPreset(),
    insertionPoint: 'jss-insertion-point',
});

if (ENVIRONMENT === 'local') {
    whyDidYouRender(React, {
        onlyLogs: true,
        titleColor: 'green',
        diffNameColor: 'darkturquoise',
    });
}
const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);

// Modified by AI on 06/29/2026. Edit #2.
root.render(
    <QueryClientProvider client={queryClient}>
        <BrowserRouter>
            <JssProvider jss={jssConfig} generateClassName={generateClassName}>
                <I18nextProvider i18n={i18n}>
                    <AppWithSettings>
                        <AppWithStaticData>
                            <TrackingApp>
                                <PublicApp>
                                    <AppWithNotification>
                                        <AuthorizedApp>
                                            <EmployerApp>
                                                <ThemedApp>
                                                    <PrintableApp>
                                                        <SharedApp>
                                                            <MobileApp>
                                                                <LoadingApp>
                                                                    <RecruiterApp>
                                                                        <PositionApp>
                                                                            <PendoApp>
                                                                                <CampaignApp>
                                                                                    <AppWithCampaignEvents>
                                                                                        <AppWithCandidatesStore>
                                                                                            <AppWithExplains>
                                                                                                <TPAHealthMeterApp>
                                                                                                    <AppWithEvents>
                                                                                                        <AppWithFeedbackStats>
                                                                                                            <AppWithGlobalState>
                                                                                                                {/* eslint-disable max-len */}
                                                                                                                <LinkedinInMailApp>
                                                                                                                    <CandidatesLinkedinStatusApp>
                                                                                                                        <AppWithCandidatesAtsSync>
                                                                                                                            <LinkedinUserInviteLimitApp>
                                                                                                                                <LinkedinMessagesSendingApp>
                                                                                                                                    <AppWithModal>
                                                                                                                                        <AppWithSidePanel>
                                                                                                                                            <AppWithAutoCampaign>
                                                                                                                                                <UnsubscribeApp>
                                                                                                                                                    <Application/>
                                                                                                                                                </UnsubscribeApp>
                                                                                                                                            </AppWithAutoCampaign>
                                                                                                                                        </AppWithSidePanel>
                                                                                                                                    </AppWithModal>
                                                                                                                                </LinkedinMessagesSendingApp>
                                                                                                                            </LinkedinUserInviteLimitApp>
                                                                                                                        </AppWithCandidatesAtsSync>
                                                                                                                    </CandidatesLinkedinStatusApp>
                                                                                                                </LinkedinInMailApp>
                                                                                                            </AppWithGlobalState>
                                                                                                        </AppWithFeedbackStats>
                                                                                                    </AppWithEvents>
                                                                                                </TPAHealthMeterApp>
                                                                                            </AppWithExplains>
                                                                                        </AppWithCandidatesStore>
                                                                                    </AppWithCampaignEvents>
                                                                                </CampaignApp>
                                                                            </PendoApp>
                                                                        </PositionApp>
                                                                    </RecruiterApp>
                                                                </LoadingApp>
                                                            </MobileApp>
                                                        </SharedApp>
                                                    </PrintableApp>
                                                </ThemedApp>
                                            </EmployerApp>
                                        </AuthorizedApp>
                                    </AppWithNotification>
                                </PublicApp>
                            </TrackingApp>
                        </AppWithStaticData>
                    </AppWithSettings>
                </I18nextProvider>
            </JssProvider>
        </BrowserRouter>
    </QueryClientProvider>,
);

document.addEventListener('DOMContentLoaded', () => {
    // eslint-disable-next-line no-undef
    document.dispatchEvent(new Event('applicationSourceLoaded'));
});

document.addEventListener('setChromeExtensionId', (e) => {
    window.ChromeExtensionId = e?.detail?.chromeExtId;
});
